import React, {useEffect, useRef, useState} from "react";
import { Box, Typography } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import {Constants, ITag} from "../graph/StateTags";
import {selectConstants} from "../../redux/selectors/firebase";
import {connect} from "react-redux";
import MarginLabel from "./MarginLabel";

const margin = "20mm";
const useScriptStyles = makeStyles(theme => ({
    container: {
        fontFamily: "inherit",
        fontSize: "12pt"
    },
    cutTo: {
    },
    dialogue: {
        marginBottom: 20,
    },
    dialogueCue: {
        fontFamily: "inherit",
        textAlign: "center",
        fontSize: "inherit",
        textTransform: "uppercase"
    },
    dialogueLine: {
        fontFamily: "inherit",
        fontSize: "inherit",
        marginLeft: "6cm",
        marginRight: "6cm"
    },
    imperative: {
        fontFamily: "inherit",
        fontSize: "inherit",
        fontWeight: "bold",
        marginLeft: margin,
        marginRight: margin,
        marginTop: theme.spacing(1)
    },
    interaction: {
        color: theme.palette.warning.main,
        fontFamily: "inherit",
        fontStyle: "italic",
        fontWeight: "bold",
        marginLeft: margin,
        marginRight: margin,
    },
    sceneHeading: {
        flexGrow: 1,
        fontFamily: "inherit",
        fontSize: "inherit",
        fontWeight: "bold",
    },
    sceneNumberLeft: {
        fontFamily: "inherit",
        fontSize: "inherit",
        fontWeight: "bold",
        marginLeft: "5mm",
        width: "15mm"
    },
    sceneNumberRight: {
        fontFamily: "inherit",
        fontSize: "inherit",
        fontWeight: "bold",
        marginRight: "5mm",
        width: "15mm"
    },
    script: {
        fontFamily: "inherit",
        fontSize: "inherit",
        fontWeight: "inherit",
        marginLeft: margin,
        marginRight: margin,
        marginBottom: 10,
    },
    transition: {
        fontFamily: "inherit",
        fontSize: "inherit",
        marginRight: margin,
        textAlign: "right",
        textTransform: "capitalize"
    },
    wrylie: {
        fontFamily: "inherit",
        textAlign: "center",
        fontSize: "inherit"
    }
}));

type Values = {[key: string]: string};

const sceneHeadingPattern = /^SCENEHEADING\((?<heading>[^)]*)\)/;
const transitionPattern = /^TRANSITION\((?<transition>[^)]*)\)/s;
const npcLinePattern = /^(?!CUT TO|TRANSITION)([A-Z\s0-9]+):\s*(.*)/s;
const npcLineParenthesisPattern = /^([A-Z\s0-9]+\(.*\)): (.*)/s;
const headingPattern = /^(INNER MONOLOGUE|TO CAMERA):\s+(.*)/s;
const matchPattern = (test: string, pattern: RegExp, callback: (match: RegExpMatchArray) => any): boolean =>
{
    const match = test?.match(pattern);

    if (match)
    {
        callback(match);
        return true;
    }
    return false;
};
interface Props
{
    id: string
    children?: React.ReactNode
    constants: Constants
    script: string
    setHeight?: (id: string, height: number) => void
    tags: ITag[]
}
function Line({ id, children, constants, script, setHeight, tags }: Props)
{
    const classes = useScriptStyles();
    const ref = useRef<HTMLDivElement>(null);
    const [characters, setCharacters] = useState<Values>({});
    const [sources, setSources] = useState<Values>({});
    const characterTag = tags.find(tag => tag.type === "character");
    const imperativeTag = tags.find(tag => tag.type === "imperative");
    const interactionTag = tags.find(tag => tag.type === "interactionName");
    const sourceTag = tags.find(tag => tag.type === "dialogueSource");
    const wrylies = tags.filter(tag => tag.type === "wrylies");
    const npcLineMatch = script.match(npcLinePattern) || script.match(npcLineParenthesisPattern);
    const buildLines = (text: string [], className: string) => text.map((line, index) => {
        let match = line.match(transitionPattern);
        
        if (match) {
            return (
                <Typography className={classes.transition} key={index}>
                    {match.groups!.transition}:
                </Typography>
            );
        }
        match = line.match(sceneHeadingPattern)
        if (match) {
            const sceneTag = tags.find(tag => tag.type === "scene");
            const scene = `Sc ${sceneTag?.value ?? ""}`;

            return (
                <Box display="flex" key={index}>
                    <MarginLabel bold={true} text={scene}/>
                    <Typography className={classes.sceneHeading} component="span">
                        {match.groups!.heading}
                    </Typography>
                    <MarginLabel bold={true} text={scene}/>
                </Box>
            );
        }
        
        return (
            <Typography className={className} key={index}>{line}</Typography>
        );
    });
    let line;
    let nodeType = null;

    if (npcLineMatch)
    {
        let header = npcLineMatch[1];
        const wryliesText = `(${wrylies.map(wrylie => wrylie.value as string).join(", ")})`
        const text = npcLineMatch[2].replace(wryliesText, "").split("\n");

        if (characterTag) {
            const character = characters[characterTag.value as string] ?? characterTag.value;

            if (sourceTag) {
                const source = sources[sourceTag.value as string] ?? sourceTag.value;

                header = `${character} (${source})`;
            } else {
                header = character;
            }
        }
        line = (
            <div className={`Dialogue ${classes.dialogue}`}>
                <Typography className={`DialogueCue ${classes.dialogueCue}`}>{header}</Typography>
                {wrylies.length > 0 &&
                    <Typography className={`Wrylie ${classes.wrylie}`}>
                        {wryliesText}
                    </Typography>
                }
                {buildLines(text, `DialogueLine ${classes.dialogueLine}`)}
            </div>
        );
    }
    else
    {
        if (! matchPattern(script, transitionPattern, cutToMatch => {
                line = (
                    <div className={`CutTo ${classes.cutTo}`}>
                        {buildLines(script.split("\n"), `Script ${classes.script}`)}
                    </div>
                );
            }) &&
            ! matchPattern(script, headingPattern, headingMatch => {
                const text = headingMatch[2].split("\n");

                line = (
                    <div className={`Dialogue ${classes.dialogue}`}>
                        <Typography className={`DialogueCue ${classes.dialogueCue}`}>{headingMatch[1]}</Typography>
                        {buildLines(text, `DialogueLine ${classes.dialogueLine}`)}
                    </div>
                );
            })) {
            line = (
                <div className={`Dialogue ${classes.dialogue}`}>
                    {buildLines(script.split("\n"), `Script ${classes.script}`)}
                    {imperativeTag &&
                        <Box display="flex" flexDirection="column">
                            <Typography className={classes.imperative}>{imperativeTag.value}</Typography>
                        </Box>
                    }
                </div>
            );
        }
    }
    if (interactionTag) {
        nodeType = <Typography className={`Interaction ${classes.interaction}`}>{interactionTag.value}</Typography>
    }

    useEffect(() => {
        if (constants) {
            if (constants.character?.constraints) {
                const select = Object.values(constants.character.constraints)
                    .find(constraint => constraint.type === "select");
    
                if (select) {
                    setCharacters(select.data.values as Values); 
                }
            }
            if (constants.dialogueSource?.constraints) {
                const select = Object.values(constants.dialogueSource.constraints)
                    .find(constraint => constraint.type === "select");
    
                if (select) {
                    setSources(select.data.values as Values); 
                }
            }
        }
    }, [constants]);
    useEffect(() =>
    {
        if (ref.current && ref.current.clientHeight > 0 && setHeight)
        {
            setHeight(id, ref.current.clientHeight + 25);
        }
    })
    return (
        <>
            <Box display="block" displayPrint="none">
                <div id={id} ref={ref} title={id} className={`NoExport ${classes.container}`}>
                    {nodeType}
                    {line}
                    {children}
                </div>
            </Box>
            <Box display="none" displayPrint="block">
                <div id={id} title={id} className={`Container ${classes.container}`}>
                    {nodeType}
                    {line}
                    {children}
                </div>
            </Box>
        </>
    );
}
const mapStateToProps = (store: never) => ({
    constants: selectConstants(store),
});
export default connect(mapStateToProps, {
})(Line);
